import { ErrorDeRegla } from './errores';

/**
 * TIEMPO
 * ======
 *
 * Dos cosas distintas que no se mezclan nunca:
 *
 *   INSTANTE        cuándo pasó algo. Se guarda en `timestamptz` y viaja en ISO-8601 con
 *                   offset explícito. No corre nada al guardar ni al recuperar.
 *   FECHA LOCAL     un día del calendario: 'YYYY-MM-DD', sin hora ni zona. "La caravana del
 *                   15" es el 15 en la zona de la entidad, no las 00:00 UTC —que en Asunción
 *                   es el 14 a las 21:00—. Se guarda en `date`.
 *
 * La zona es un DATO DE LA ENTIDAD (`zonaHoraria`, nombre IANA), nunca una constante ni un
 * offset fijo: Paraguay tuvo horario de verano hasta 2024, así que un '-03:00' escrito a
 * mano corre una hora sobre cualquier fecha anterior. El nombre IANA conoce esa historia;
 * el número, no.
 */
export type FechaLocal = string;

const FORMATO = /^(\d{4})-(\d{2})-(\d{2})$/;

export function esFechaLocal(valor: string): boolean {
  const partes = FORMATO.exec(valor);
  if (!partes) return false;
  const [anio, mes, dia] = [Number(partes[1]), Number(partes[2]), Number(partes[3])];
  if (mes < 1 || mes > 12 || dia < 1) return false;
  return dia <= diasDelMes(anio, mes);
}

export function asegurarFecha(valor: string, nombre = 'La fecha'): FechaLocal {
  if (!esFechaLocal(valor)) {
    throw new ErrorDeRegla(`${nombre} no es una fecha válida (recibí "${valor}").`);
  }
  return valor;
}

function partir(fecha: FechaLocal): [number, number, number] {
  asegurarFecha(fecha);
  const [anio, mes, dia] = fecha.split('-').map(Number) as [number, number, number];
  return [anio, mes, dia];
}

function componer(anio: number, mes: number, dia: number): FechaLocal {
  return `${String(anio).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

export function diasDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0)).getUTCDate();
}

/** Días desde la época, contados sobre el calendario. Sin zonas de por medio. */
function numeroDeDia(fecha: FechaLocal): number {
  const [anio, mes, dia] = partir(fecha);
  return Math.round(Date.UTC(anio, mes - 1, dia) / 86_400_000);
}

function desdeNumeroDeDia(numero: number): FechaLocal {
  return new Date(numero * 86_400_000).toISOString().slice(0, 10);
}

export function sumarDias(fecha: FechaLocal, dias: number): FechaLocal {
  return desdeNumeroDeDia(numeroDeDia(fecha) + dias);
}

/** `hasta - desde` en días. Positivo si `hasta` es posterior. */
export function diferenciaEnDias(desde: FechaLocal, hasta: FechaLocal): number {
  return numeroDeDia(hasta) - numeroDeDia(desde);
}

export function compararFechas(a: FechaLocal, b: FechaLocal): number {
  // El formato ISO ordena lexicográficamente igual que el calendario.
  return a < b ? -1 : a > b ? 1 : 0;
}

export function inicioDeMes(fecha: FechaLocal): FechaLocal {
  const [anio, mes] = partir(fecha);
  return componer(anio, mes, 1);
}

export function finDeMes(fecha: FechaLocal): FechaLocal {
  const [anio, mes] = partir(fecha);
  return componer(anio, mes, diasDelMes(anio, mes));
}

/**
 * La fecha de HOY en la zona de la entidad.
 *
 * Es la única puerta entre el reloj (un instante) y el calendario (un día). Se le pasa la
 * zona como dato; nunca se usa la del servidor ni la del navegador. Lo que el mostrador
 * llama "los pagos de hoy" se calcula con esto.
 */
export function hoyEnZona(zona: string, ahora: Date = new Date()): FechaLocal {
  return fechaLocalDeInstante(ahora, zona);
}

export function fechaLocalDeInstante(instante: Date, zona: string): FechaLocal {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zona,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instante);
}

/**
 * Minutos que la zona está CORRIDA respecto de UTC en ese instante: hora local menos UTC.
 * Asunción hoy da **-180** (al oeste, negativo), como el offset que escribe ISO-8601.
 *
 * El signo se aclara porque es la trampa clásica: `Date.getTimezoneOffset()` de JavaScript
 * devuelve lo contrario (+180 para Asunción) y mezclar las dos convenciones corre seis horas,
 * no tres.
 */
export function offsetEnZona(instante: Date, zona: string): number {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: zona, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instante);
  const leer = (tipo: Intl.DateTimeFormatPartTypes) => Number(partes.find((p) => p.type === tipo)?.value ?? 0);
  const hora = leer('hour') === 24 ? 0 : leer('hour');
  const comoSiFueraUtc = Date.UTC(leer('year'), leer('month') - 1, leer('day'), hora, leer('minute'), leer('second'));
  return Math.round((comoSiFueraUtc - instante.getTime()) / 60_000);
}

/** El offset en formato ISO: -180 → "-03:00". Para devolver instantes con offset a la vista. */
export function textoOffset(minutos: number): string {
  const signo = minutos < 0 ? '-' : '+';
  const absoluto = Math.abs(minutos);
  return `${signo}${String(Math.floor(absoluto / 60)).padStart(2, '0')}:${String(absoluto % 60).padStart(2, '0')}`;
}

/**
 * El instante en que EMPIEZA un día local en la zona de la entidad.
 *
 * Con el nombre IANA, nunca con un offset fijo. Dos pasadas por si la primera estimación
 * cae del otro lado de un cambio de horario (aprendido en PLES): se estima el offset, se
 * ajusta, y si el offset del resultado cambió se rehace con el nuevo.
 */
export function inicioDelDiaEnZona(fecha: FechaLocal, zona: string): Date {
  const [anio, mes, dia] = partir(fecha);
  const tentativo = new Date(Date.UTC(anio, mes - 1, dia));
  const primero = offsetEnZona(tentativo, zona);
  const ajustado = new Date(tentativo.getTime() - primero * 60_000);
  const segundo = offsetEnZona(ajustado, zona);
  return segundo === primero ? ajustado : new Date(tentativo.getTime() - segundo * 60_000);
}

/** Los dos extremos del rango [desde, hasta] de días locales, como instantes. `hasta` inclusive. */
export function rangoDeDiasEnZona(
  desde: FechaLocal | undefined,
  hasta: FechaLocal | undefined,
  zona: string,
): { desde?: Date; hasta?: Date } {
  return {
    ...(desde ? { desde: inicioDelDiaEnZona(desde, zona) } : {}),
    // Exclusivo del día siguiente: así el último día entra completo sin depender de
    // milisegundos, que es donde siempre se pierde el pago de las 23:59.
    ...(hasta ? { hasta: inicioDelDiaEnZona(sumarDias(hasta, 1), zona) } : {}),
  };
}

const CON_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * EL ÚNICO LUGAR QUE INTERPRETA UN DATETIME QUE LLEGÓ SIN ZONA.
 *
 * Si la entrada trae `Z` o `±HH:MM`, ya es un instante y se respeta tal cual. Si NO trae
 * offset —el caso típico de un `<input type="datetime-local">`— se interpreta como hora
 * local de la zona del tenant y se convierte a UTC.
 *
 * Que esto viva en una sola función es la mitad importante de la regla: si tres capas cada
 * una "asume Paraguay", el instante se corre tres veces. Esa es la causa real de los
 * corrimientos, no la suposición en sí.
 *
 * Casos borde de un cambio de horario (solo fechas anteriores a 2024 en Paraguay):
 *   · hora ambigua (ocurre dos veces) → se toma la PRIMERA ocurrencia;
 *   · hora inexistente (se saltó)     → se corre hacia adelante.
 * Las dos salen solas del ajuste por offset de abajo; están escritas para que la próxima
 * persona sepa que fue una decisión y no un accidente.
 */
export function instanteDesdeEntrada(texto: string, zona: string): Date {
  const valor = texto.trim();
  if (valor.length === 0) throw new ErrorDeRegla('Falta la fecha y hora.');

  if (CON_OFFSET.test(valor)) {
    const instante = new Date(valor);
    if (Number.isNaN(instante.getTime())) throw new ErrorDeRegla(`La fecha y hora no es válida (recibí "${texto}").`);
    return instante;
  }

  const partes = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(valor);
  if (!partes) {
    throw new ErrorDeRegla(`La fecha y hora no es válida (recibí "${texto}"). Se espera AAAA-MM-DDTHH:MM.`);
  }
  const [anio, mes, dia] = [Number(partes[1]), Number(partes[2]), Number(partes[3])];
  const [hora, minuto, segundo] = [Number(partes[4] ?? 0), Number(partes[5] ?? 0), Number(partes[6] ?? 0)];
  asegurarFecha(componer(anio, mes, dia));

  const comoSiFueraUtc = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo);
  const primero = offsetEnZona(new Date(comoSiFueraUtc), zona);
  const ajustado = new Date(comoSiFueraUtc - primero * 60_000);
  const segundoOffset = offsetEnZona(ajustado, zona);
  return segundoOffset === primero ? ajustado : new Date(comoSiFueraUtc - segundoOffset * 60_000);
}

/**
 * El instante normalizado como lo devuelve la API: ISO-8601 con el offset de la entidad.
 *
 * Se devuelve con offset y no con 'Z' para que quien mira la respuesta VEA qué se asumió.
 * Un default invisible es el que se descubre tarde.
 */
export function isoEnZona(instante: Date, zona: string): string {
  const minutos = offsetEnZona(instante, zona);
  // hora local = UTC + offset, y el offset de Asunción es negativo: sumar es correr hacia atrás.
  const local = new Date(instante.getTime() + minutos * 60_000);
  return `${local.toISOString().slice(0, 19)}${textoOffset(minutos)}`;
}

/** La hora local en la zona de la entidad: "18:42". Para el comprobante y el mostrador. */
export function horaEnZona(instante: Date, zona: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: zona, hour12: false, hour: '2-digit', minute: '2-digit',
  }).format(instante);
}

/** Cómo se lee una fecha local en pantalla: '2026-10-15' → '15/10/2026'. */
export function formatearFecha(fecha: FechaLocal): string {
  const [anio, mes, dia] = fecha.split('-');
  return `${dia}/${mes}/${anio}`;
}

export function esZonaValida(zona: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zona });
    return true;
  } catch {
    return false;
  }
}
